import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { COMPANY } from '../constants';

const PolitiqueConfidentialite = () => {
  useEffect(() => {
    document.title = "Politique de Confidentialité | Reco Pneus";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="politique-confidentialite-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#41DB23] text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-gray-900 mb-8">
            Politique de Confidentialité
          </h1>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">1. Introduction</h2>
              <p className="text-gray-600">
                Reco Pneus s'engage à protéger la vie privée des utilisateurs de son site internet. 
                Cette politique de confidentialité explique comment nous collectons, utilisons et 
                protégeons vos données personnelles conformément au Règlement Général sur la 
                Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">2. Responsable du traitement</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-gray-600">
                <p><strong className="text-gray-900">Reco Pneus</strong></p>
                <p>Email : {COMPANY.email}</p>
                <p>Téléphone : {COMPANY.phone}</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">3. Données collectées</h2>
              <p className="text-gray-600">Nous collectons les données suivantes :</p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li><strong className="text-gray-900">Données d'identification :</strong> nom, prénom</li>
                <li><strong className="text-gray-900">Données de contact :</strong> adresse email, numéro de téléphone</li>
                <li><strong className="text-gray-900">Données de navigation :</strong> adresse IP, cookies, pages visitées</li>
                <li><strong className="text-gray-900">Données relatives à votre demande :</strong> type de service souhaité, description de votre situation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">4. Finalités du traitement</h2>
              <p className="text-gray-600">Vos données sont collectées pour :</p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li>Traiter vos demandes de devis et d'intervention</li>
                <li>Vous recontacter pour répondre à vos questions</li>
                <li>Améliorer notre site et nos services</li>
                <li>Respecter nos obligations légales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">5. Base légale du traitement</h2>
              <p className="text-gray-600">
                Le traitement de vos données repose sur votre consentement (formulaire de contact) 
                et sur l'exécution de mesures précontractuelles (demande de devis).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">6. Durée de conservation</h2>
              <p className="text-gray-600">
                Vos données personnelles sont conservées pendant une durée de 3 ans à compter de 
                votre dernier contact avec nous, sauf obligation légale de conservation plus longue.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">7. Destinataires des données</h2>
              <p className="text-gray-600">
                Vos données sont destinées uniquement au personnel de Reco Pneus. 
                Elles ne sont jamais vendues ni transmises à des tiers à des fins commerciales.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">8. Vos droits</h2>
              <p className="text-gray-600">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li><strong className="text-gray-900">Droit d'accès :</strong> obtenir la confirmation que vos données sont traitées et en recevoir une copie</li>
                <li><strong className="text-gray-900">Droit de rectification :</strong> demander la correction de données inexactes</li>
                <li><strong className="text-gray-900">Droit à l'effacement :</strong> demander la suppression de vos données</li>
                <li><strong className="text-gray-900">Droit à la limitation :</strong> demander la limitation du traitement de vos données</li>
                <li><strong className="text-gray-900">Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                <li><strong className="text-gray-900">Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Pour exercer ces droits, contactez-nous à : {COMPANY.email}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">9. Sécurité des données</h2>
              <p className="text-gray-600">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées 
                pour protéger vos données contre tout accès non autorisé, perte ou destruction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">10. Réclamation</h2>
              <p className="text-gray-600">
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une 
                réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : 
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#41DB23] hover:underline ml-1">
                  www.cnil.fr
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#41DB23] mb-4">11. Modification de la politique</h2>
              <p className="text-gray-600">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
                Les modifications prennent effet dès leur publication sur le site.
              </p>
            </section>
          </div>

          <p className="text-gray-400 text-sm mt-12">
            Dernière mise à jour : Janvier 2025
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
